import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/firebase/api-client";

/**
 * ===========================================================================
 *  SAISIR LE BON DE LIVRAISON DES EMBALLAGES
 * ===========================================================================
 *
 *  Ce qui manquait. On ne saisissait que la REMISE — « on a donné 1000
 *  sachets à bouchaib ». La facture du fabricant, elle, n'entrait nulle
 *  part : ni son numéro, ni sa date, ni ce qu'elle avait coûté.
 *
 *  Résultat : on savait ce qu'on avait donné, jamais ce qu'on avait payé ni
 *  ce qu'il restait en main. Aucun rapprochement n'était possible.
 *
 *  ---------------------------------------------------------------------
 *  LE CIRCUIT, TEL QU'IL EST VRAIMENT
 *  ---------------------------------------------------------------------
 *
 *    1. la livraison arrive CHEZ LE RESPONSABLE COMMANDE d'un magasin ;
 *    2. il en remet une partie aux fournisseurs ;
 *    3. le reste demeure son stock.
 *
 *  L'écran suit ce circuit dans cet ordre : d'abord le papier (numéro,
 *  date, magasin), puis les lignes, puis qui reçoit quoi. Le reste se
 *  calcule sous les yeux — on ne le saisit pas, on le VOIT.
 * ===========================================================================
 */

type Ventilation = { fournisseurId: string; quantite: string };
type Ligne = {
  emballage: string;
  quantite: string;
  prixUnitaire: string;
  /**
   * Le total de la ligne tel qu'il est ecrit sur la facture, quand on le
   * saisit directement. Le prix unitaire en est alors deduit, avec toutes
   * ses decimales : 72 DH les 412 sachets = 0,174757 DH le sachet.
   */
  totalLigne?: string;
  ventilation: Ventilation[];
};

/** Le total d'une ligne en centimes : seul le TOTAL s'arrondit, jamais le prix unitaire. */
const centimesLigne = (l: Ligne) =>
  Math.round(nombre(l.prixUnitaire) * 100 * nombre(l.quantite));

/** Le prix unitaire qui redonne exactement le total saisi. */
const prixDepuisTotal = (total: string, quantite: string) => {
  const q = nombre(quantite);
  const t = nombre(total);
  if (!(q > 0) || !(t >= 0) || !total.trim()) return "";
  return String(Number((t / q).toFixed(6))).replace(".", ",");
};

/**
 * LE RESPONSABLE COMMANDE, COMME DESTINATAIRE.
 *
 * Toute la quantité livrée doit être affectée — sinon le bouton reste gris.
 * Sans cette entrée, la règle serait intenable : une part reste toujours en
 * stock, et il faut pouvoir le DIRE au lieu de laisser un trou.
 */
const PART_MAGASIN = "MAGASIN";

/** « Sac Kraft 2 KG » et « sac kraft 2 kg » sont le meme sac. */
const reduire = (v: string) => v.trim().toLowerCase().replace(/\s+/g, " ");

/**
 * LES EMBALLAGES QUI NE SORTENT PAS DU MAGASIN.
 *
 * Le sac client et le sac en tissu ne sont pas confies a un fournisseur :
 * ils partent avec la commande du client. Les faire ventiler a la main,
 * c'est demander chaque fois la meme reponse — et bloquer le bouton tant
 * qu'on ne l'a pas donnee. Ils vont d'office au responsable commande.
 */
const EMBALLAGES_DU_MAGASIN = ["sac craft clt", "emballage tissu"];
const ensembleMagasin = new Set(EMBALLAGES_DU_MAGASIN.map(reduire));
const estDuMagasin = (nom: string) => ensembleMagasin.has(reduire(nom));

/** Remplace par « sac craft clt » : c'est le meme sac, mieux nomme. */
const RETIRES_DE_LA_LISTE = new Set(["sac kraft", "sac kraft 2 kg"].map(reduire));

const ligneVide = (): Ligne => ({
  emballage: "",
  quantite: "",
  prixUnitaire: "",
  ventilation: [],
});

/** « 1 234,50 » — jamais « 1234.5 » sur un écran qu'on lit vite. */
const dh = (centimes: number) =>
  (centimes / 100).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const nombre = (v: string) => {
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

export default function BonLivraisonEmballages({
  magasins,
  fournisseurs,
  emballagesConnus,
  onEnregistre,
}: {
  magasins: Array<{ id: string; name: string }>;
  /** Tous les fournisseurs, avec leur magasin : on filtre sur celui choisi. */
  fournisseurs: Array<{ id: string; nom: string; storeId: string | null }>;
  /** Les noms d'emballages déjà vus. Une liste, pas une contrainte. */
  emballagesConnus: string[];
  onEnregistre?: () => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [numeroFacture, setNumeroFacture] = useState("");
  const [le, setLe] = useState(() =>
    new Date().toLocaleDateString("en-CA", { timeZone: "Africa/Casablanca" }),
  );
  const [storeId, setStoreId] = useState("");
  const [fabricant, setFabricant] = useState("");
  const [lignes, setLignes] = useState<Ligne[]>([ligneVide()]);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [signal, setSignal] = useState<"repos" | "envoi" | "envoye">("repos");

  useEffect(() => {
    if (!storeId && magasins.length > 0) setStoreId(magasins[0].id);
  }, [magasins, storeId]);

  // Changer de magasin vide les ventilations : un fournisseur de Bourgogne
  // n'a rien à voir avec une livraison d'Oulfa, et laisser son nom dans la
  // case donnerait une erreur au moment d'enregistrer, pas avant.
  useEffect(() => {
    setLignes((ls) => ls.map((l) => ({ ...l, ventilation: [] })));
  }, [storeId]);

  /**
   * CE QUE PROPOSE LA LISTE. Les deux emballages du magasin en tete — ce
   * sont les plus frequents — puis ce qu'on a deja vu passer, sans doublon.
   */
  const choixEmballages = useMemo(() => {
    const vus = new Set<string>();
    const sortie: string[] = [];
    for (const e of [...EMBALLAGES_DU_MAGASIN, ...emballagesConnus]) {
      const r = reduire(e);
      if (!r || RETIRES_DE_LA_LISTE.has(r) || vus.has(r)) continue;
      vus.add(r);
      sortie.push(e);
    }
    return sortie;
  }, [emballagesConnus]);

  /**
   * L'AFFECTATION D'OFFICE. Des qu'une ligne porte un emballage du magasin,
   * sa quantite entiere va au responsable commande. On ne reecrit que si la
   * valeur change vraiment : sinon cet effet se rappellerait sans fin.
   */
  useEffect(() => {
    setLignes((ls) => {
      let change = false;
      const suite = ls.map((l) => {
        if (!estDuMagasin(l.emballage)) return l;
        const voulu =
          nombre(l.quantite) > 0
            ? [{ fournisseurId: PART_MAGASIN, quantite: l.quantite }]
            : [];
        const identique =
          l.ventilation.length === voulu.length &&
          l.ventilation.every(
            (v, k) =>
              v.fournisseurId === voulu[k].fournisseurId &&
              v.quantite === voulu[k].quantite,
          );
        if (identique) return l;
        change = true;
        return { ...l, ventilation: voulu };
      });
      return change ? suite : ls;
    });
  }, [lignes]);

  const siens = useMemo(
    () => fournisseurs.filter((f) => !f.storeId || f.storeId === storeId),
    [fournisseurs, storeId],
  );

  /** Ce qui est affecté sur une ligne, responsable commande compris. */
  const affecte = (l: Ligne) =>
    l.ventilation.reduce((t, v) => t + nombre(v.quantite), 0);

  /** Les lignes qui ne tombent pas juste. Vide = on peut enregistrer. */
  const ecarts = useMemo(
    () =>
      lignes
        .filter((l) => l.emballage.trim() && nombre(l.quantite) > 0)
        .map((l) => ({
          emballage: l.emballage.trim(),
          livre: nombre(l.quantite),
          affecte: affecte(l),
        }))
        .filter((e) => e.affecte !== e.livre),
    [lignes],
  );

  const utilisables = lignes.filter(
    (l) => l.emballage.trim() && nombre(l.quantite) > 0,
  ).length;
  const equilibre = utilisables > 0 && ecarts.length === 0;

  const totalCentimes = lignes.reduce((t, l) => t + centimesLigne(l), 0);

  const majLigne = (i: number, champ: keyof Ligne, valeur: any) =>
    setLignes((ls) => ls.map((l, k) => (k === i ? { ...l, [champ]: valeur } : l)));

  const majVent = (i: number, j: number, champ: keyof Ventilation, v: string) =>
    setLignes((ls) =>
      ls.map((l, k) =>
        k === i
          ? {
              ...l,
              ventilation: l.ventilation.map((x, m) =>
                m === j ? { ...x, [champ]: v } : x,
              ),
            }
          : l,
      ),
    );

  /** Amina prévient le patron : la facture n'est pas passée, et pourquoi. */
  const prevenir = async () => {
    try {
      setSignal("envoi");
      await api.emballages.signalerBlocage({
        numeroFacture: numeroFacture.trim(),
        storeId,
        ecarts,
      });
      setSignal("envoye");
    } catch {
      // Même en cas d'échec on n'ajoute pas une erreur de plus à l'écran
      // de quelqu'un qui est déjà bloqué.
      setSignal("repos");
    }
  };

  const enregistrer = async () => {
    setErreur(null);

    if (!numeroFacture.trim()) return setErreur("Le numéro de facture manque.");
    if (!storeId) return setErreur("Choisis le magasin qui reçoit la livraison.");

    const utiles = lignes.filter(
      (l) => l.emballage.trim() && nombre(l.quantite) > 0,
    );
    if (utiles.length === 0)
      return setErreur("Aucune ligne : il faut au moins un emballage.");

    // Le contrôle qui compte, fait ICI avant l'aller-retour : chaque
    // quantité livrée doit être entièrement affectée, ni plus ni moins.
    for (const l of utiles) {
      const a = affecte(l);
      if (a !== nombre(l.quantite)) {
        return setErreur(
          `« ${l.emballage} » : ${a} affectés pour ${nombre(l.quantite)} livrés.`,
        );
      }
    }

    try {
      setEnvoi(true);
      await api.emballages.bon({
        numeroFacture: numeroFacture.trim(),
        le: new Date(`${le}T12:00:00`).toISOString(),
        storeId,
        fabricant: fabricant.trim() || undefined,
        lignes: utiles.map((l) => ({
          emballage: l.emballage.trim(),
          quantite: nombre(l.quantite),
          prixUnitaire: l.prixUnitaire ? nombre(l.prixUnitaire) : undefined,
          ventilation: l.ventilation
            .filter((v) => v.fournisseurId && nombre(v.quantite) > 0)
            .map((v) => ({
              fournisseurId: v.fournisseurId,
              quantite: nombre(v.quantite),
            })),
        })),
      });

      setNumeroFacture("");
      setFabricant("");
      setLignes([ligneVide()]);
      setOuvert(false);
      onEnregistre?.();
    } catch (e: any) {
      // On dit POURQUOI. Un bouton qui ne fait rien, sans un mot, laisse
      // croire que c'est passé.
      setErreur(e?.message || "Enregistrement impossible.");
    } finally {
      setEnvoi(false);
    }
  };

  if (!ouvert) {
    return (
      <button
        type="button"
        onClick={() => setOuvert(true)}
        className="w-full rounded-2xl border-2 border-dashed border-gray-300 px-4 py-4 text-sm font-bold text-gray-600 hover:border-gray-400 hover:bg-gray-50"
      >
        + Saisir un bon de livraison d&apos;emballages
      </button>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-5 space-y-4">
      <div className="flex items-baseline gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Bon de livraison d&apos;emballages
        </p>
        <button
          type="button"
          onClick={() => setOuvert(false)}
          className="ml-auto text-xs font-semibold text-gray-500 hover:text-gray-800"
        >
          Fermer
        </button>
      </div>

      {/* L'EN-TETE : ce qui est écrit sur le papier. */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <label className="text-xs font-semibold text-gray-600">
          N° de facture
          <input
            value={numeroFacture}
            onChange={(e) => setNumeroFacture(e.target.value)}
            placeholder="00005880/2026"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal"
          />
        </label>
        <label className="text-xs font-semibold text-gray-600">
          Date de la facture
          <input
            type="date"
            value={le}
            onChange={(e) => setLe(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal"
          />
        </label>
        <label className="text-xs font-semibold text-gray-600">
          Magasin qui reçoit
          <select
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal bg-white"
          >
            {magasins.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-gray-600">
          Fabricant (facultatif)
          <input
            value={fabricant}
            onChange={(e) => setFabricant(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal"
          />
        </label>
      </div>

      <datalist id="emballages-connus">
        {choixEmballages.map((e) => (
          <option key={e} value={e} />
        ))}
      </datalist>

      {/* LES LIGNES, ET QUI RECOIT QUOI. */}
      <div className="space-y-3">
        {lignes.map((l, i) => {
          const q = nombre(l.quantite);
          const auMagasin = estDuMagasin(l.emballage);
          const ventile = l.ventilation.reduce(
            (t, v) => t + nombre(v.quantite),
            0,
          );
          const reste = q - ventile;
          const trop = ventile > q;

          return (
            <div
              key={i}
              className={`rounded-xl border px-3 py-3 ${
                trop
                  ? "border-red-400 bg-red-50"
                  : q > 0 && reste > 0
                    ? "border-orange-300 bg-orange-50/50"
                    : "border-gray-200 bg-gray-50/60"
              }`}
            >
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                <label className="col-span-2 text-xs font-semibold text-gray-600">
                  Emballage
                  <input
                    list="emballages-connus"
                    value={l.emballage}
                    onChange={(e) => majLigne(i, "emballage", e.target.value)}
                    placeholder="sac craft clt"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal"
                  />
                </label>
                <label className="text-xs font-semibold text-gray-600">
                  Quantité
                  <input
                    inputMode="numeric"
                    value={l.quantite}
                    onChange={(e) => {
                      const v = e.target.value;
                      // Total saisi depuis la facture : il fait foi, le prix
                      // unitaire suit la quantite.
                      setLignes((ls) => ls.map((x, k) => k !== i ? x : {
                        ...x,
                        quantite: v,
                        ...(x.totalLigne !== undefined
                          ? { prixUnitaire: prixDepuisTotal(x.totalLigne, v) }
                          : {}),
                      }));
                    }}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal"
                  />
                </label>
                <label className="text-xs font-semibold text-gray-600">
                  Prix unitaire
                  <input
                    inputMode="decimal"
                    value={l.prixUnitaire}
                    onChange={(e) => {
                      const v = e.target.value;
                      setLignes((ls) => ls.map((x, k) => k !== i ? x : {
                        ...x,
                        prixUnitaire: v,
                        totalLigne: undefined,
                      }));
                    }}
                    placeholder="0,70"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal"
                  />
                </label>
                <label className="text-xs font-semibold text-gray-600">
                  Total de la ligne
                  <input
                    inputMode="decimal"
                    value={
                      l.totalLigne !== undefined
                        ? l.totalLigne
                        : l.prixUnitaire && q > 0
                          ? dh(centimesLigne(l))
                          : ""
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      setLignes((ls) => ls.map((x, k) => k !== i ? x : {
                        ...x,
                        totalLigne: v,
                        prixUnitaire: prixDepuisTotal(v, x.quantite),
                      }));
                    }}
                    placeholder="comme sur la facture"
                    title="Tapez le total écrit sur la facture : le prix unitaire se calcule tout seul."
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-bold text-right"
                  />
                </label>
              </div>

              {/* LA VENTILATION. Ce qui n'est donné à personne reste au
                  responsable commande — et l'écran le dit, au lieu de
                  laisser croire que tout est parti.

                  Sauf pour les emballages du magasin : eux n'ont rien à
                  ventiler, et un choix qui n'a qu'une réponse n'est pas un
                  choix — c'est un obstacle. */}
              {auMagasin ? (
                <p className="mt-3 pl-1 border-l-2 border-emerald-300 text-xs font-semibold text-emerald-800">
                  {q > 0
                    ? `✓ ${q} remis au responsable commande`
                    : "Remis au responsable commande — indique la quantité"}
                </p>
              ) : (
              <div className="mt-3 pl-1 border-l-2 border-gray-200 space-y-2">
                {l.ventilation.map((v, j) => (
                  <div key={j} className="flex flex-wrap items-center gap-2">
                    <select
                      value={v.fournisseurId}
                      onChange={(e) =>
                        majVent(i, j, "fournisseurId", e.target.value)
                      }
                      className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm bg-white"
                    >
                      <option value="">— à qui ? —</option>
                      {/* En tête, parce que c'est le cas le plus fréquent :
                          ce que personne ne prend reste en stock. */}
                      <option value={PART_MAGASIN}>
                        Responsable commande (stock)
                      </option>
                      {siens.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.nom}
                        </option>
                      ))}
                    </select>
                    <input
                      inputMode="numeric"
                      value={v.quantite}
                      onChange={(e) => majVent(i, j, "quantite", e.target.value)}
                      placeholder="qté"
                      className="w-24 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        majLigne(
                          i,
                          "ventilation",
                          l.ventilation.filter((_, m) => m !== j),
                        )
                      }
                      className="text-xs font-semibold text-red-600 hover:text-red-800"
                    >
                      retirer
                    </button>
                  </div>
                ))}

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      majLigne(i, "ventilation", [
                        ...l.ventilation,
                        { fournisseurId: "", quantite: "" },
                      ])
                    }
                    className="text-xs font-bold text-blue-700 hover:text-blue-900"
                  >
                    + donner à un fournisseur
                  </button>

                  {q > 0 && (
                    <span
                      className={`text-xs font-semibold ${
                        reste === 0
                          ? "text-emerald-700"
                          : trop
                            ? "text-red-700"
                            : "text-orange-700"
                      }`}
                    >
                      {reste === 0
                        ? `✓ ${q} affectés sur ${q}`
                        : trop
                          ? `${ventile} affectés pour ${q} livrés — ${-reste} de trop`
                          : `${ventile} affectés sur ${q} — il reste ${reste} à placer`}
                    </span>
                  )}
                </div>
              </div>
              )}
            </div>
          );
        })}

        <div className="flex flex-wrap items-center gap-3">
          {/* UNE FACTURE A PRESQUE TOUJOURS PLUSIEURS EMBALLAGES.
              En lien discret, on ne le voyait pas — et le menu déroulant
              de la ligne du dessus passait par-dessus. */}
          <button
            type="button"
            onClick={() => setLignes((ls) => [...ls, ligneVide()])}
            className="rounded-xl border-2 border-dashed border-gray-300 px-4 py-2 text-sm font-bold text-gray-700 hover:border-gray-400 hover:bg-gray-50"
          >
            + ajouter un autre emballage
          </button>
          <span className="ml-auto text-base font-black text-gray-900">
            Total : {dh(totalCentimes)} DH
          </span>
        </div>
      </div>

      {erreur && (
        <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-800">
          {erreur}
        </p>
      )}

      {/* LE BOUTON RESTE GRIS TANT QUE LA FACTURE NE TOMBE PAS JUSTE.
          Une facture à moitié ventilée entre en base comme si elle était
          juste, et l'écart ne se voit plus jamais. */}
      <button
        type="button"
        disabled={envoi || !equilibre}
        onClick={() => void enregistrer()}
        className={`w-full rounded-xl px-4 py-3 text-sm font-bold ${
          equilibre
            ? "bg-slate-900 text-white hover:bg-slate-800"
            : "bg-gray-200 text-gray-500 cursor-not-allowed"
        } disabled:opacity-60`}
      >
        {envoi
          ? "…"
          : equilibre
            ? "Enregistrer le bon de livraison"
            : "Affecte toutes les quantités pour enregistrer"}
      </button>

      {/* LA SORTIE. Un bouton gris, seul, est un cul-de-sac : la personne
          abandonne la saisie et personne n'apprend que la facture n'est
          pas passée. */}
      {!equilibre && utilisables > 0 && (
        <button
          type="button"
          disabled={signal !== "repos"}
          onClick={() => void prevenir()}
          className="w-full rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-2.5 text-sm font-bold text-amber-900 hover:bg-amber-100 disabled:opacity-60"
        >
          {signal === "envoi"
            ? "…"
            : signal === "envoye"
              ? "✓ Signalé — la direction le verra dans le bilan du soir"
              : "Je n'arrive pas à équilibrer — signaler à la direction"}
        </button>
      )}
    </div>
  );
}
